import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Plus, Edit, Trash2, Eye, EyeOff, Package, CheckCircle, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const DashboardPage = () => {
  const { user, loading: authLoading, api, isSubscribed, getTrialDaysLeft, canSell } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_seller) {
      navigate('/subscription');
      return;
    }
    fetchListings();
  }, [user, authLoading]);

  const fetchListings = async () => {
    try {
      const res = await api().get('/my-listings');
      setListings(res.data);
    } catch (err) {
      console.error('Failed to fetch listings', err);
      toast.error('Failed to load your listings');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api().delete(`/listings/${id}`);
      setListings(listings.filter(l => l.id !== id));
      toast.success('Listing deleted');
    } catch (err) {
      console.error('Failed to delete listing', err);
      toast.error('Failed to delete listing');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleStatus = async (listing) => {
    const newStatus = listing.status === 'active' ? 'paused' : 'active';
    try {
      await api().put(`/listings/${listing.id}`, { status: newStatus });
      setListings(listings.map(l => l.id === listing.id ? { ...l, status: newStatus } : l));
      toast.success(`Listing ${newStatus === 'active' ? 'activated' : 'paused'}`);
    } catch (err) {
      console.error('Failed to update listing', err);
      toast.error('Failed to update listing');
    }
  };

  const markAsSold = async (listing) => {
    try {
      await api().put(`/listings/${listing.id}/sold`);
      setListings(listings.map(l => l.id === listing.id ? { ...l, status: 'sold' } : l));
      toast.success('Item marked as sold!');
    } catch (err) {
      console.error('Failed to mark as sold', err);
      toast.error('Failed to mark as sold');
    }
  };

  const markAsUnsold = async (listing) => {
    try {
      await api().put(`/listings/${listing.id}/unsold`);
      setListings(listings.map(l => l.id === listing.id ? { ...l, status: 'active' } : l));
      toast.success('Item marked as available!');
    } catch (err) {
      console.error('Failed to mark as available', err);
      toast.error('Failed to mark as available');
    }
  };

  const trialDays = getTrialDaysLeft();
  const subscribed = isSubscribed();
  const sellerCanList = user?.is_seller && subscribed;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              My Listings
            </h1>
            <p className="text-stone-500 mt-1">
              Manage your items for sale
            </p>
          </div>
          <Link to="/create-listing">
            <Button
              className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium tracking-wide"
              disabled={!sellerCanList}
              data-testid="create-listing-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Listing
            </Button>
          </Link>
        </div>

        {/* Promo Code Active Banner - For LAUNCH26, CM2026, etc. */}
        {user && user.subscription_status === 'trial' && trialDays > 0 && user.promo_code_used && user.promo_code_used !== 'CMFFL26EF' && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 mb-6 flex items-center gap-3">
            <span className="text-2xl">✨</span>
            <div>
              <p className="font-medium text-emerald-800">
                <span className="font-mono bg-emerald-100 px-2 py-0.5 rounded">{user.promo_code_used}</span> active
              </p>
              <p className="text-sm text-emerald-600">{trialDays} days remaining - enjoy your free access!</p>
            </div>
          </div>
        )}

        {/* Trial Banner - Only for users WITHOUT a promo code */}
        {user && user.subscription_status === 'trial' && trialDays > 0 && !user.promo_code_used && (
          <div className="bg-teal-50 border border-teal-200 rounded-sm p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="font-medium text-teal-700">
                {trialDays} days left in your free trial
              </p>
              <p className="text-sm text-teal-600">
                Subscribe now to continue listing after your trial ends.
              </p>
            </div>
            <Link to="/subscription">
              <Button variant="outline" className="border-teal-500 text-teal-600 hover:bg-teal-100">
                View Plans
              </Button>
            </Link>
          </div>
        )}

        {/* VIP Status Banner */}
        {user && (user.subscription_status === 'free_for_life' || user.promo_code_used === 'CMFFL26EF') && (
          <div className="bg-purple-50 border border-purple-200 rounded-sm p-4 mb-6 flex items-center gap-3">
            <span className="text-2xl">🌟</span>
            <div>
              <p className="font-medium text-purple-800">VIP Member - Free for Life</p>
              <p className="text-sm text-purple-600">You have unlimited access to all seller features!</p>
            </div>
          </div>
        )}

        {/* Trial Ended Banner - Only for users WITHOUT promo code */}
        {!subscribed && user?.subscription_status === 'trial' && trialDays <= 0 && !user.promo_code_used && (
          <div className="bg-stone-100 border border-stone-200 rounded-sm p-6 mb-6 text-center">
            <h3 className="font-semibold text-stone-900 mb-2">Your trial has ended</h3>
            <p className="text-stone-500 mb-4">Subscribe to continue creating and managing listings.</p>
            <Link to="/subscription">
              <Button className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white">
                Subscribe Now
              </Button>
            </Link>
          </div>
        )}

        {/* Listings */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white border border-stone-200 rounded-sm h-24 animate-pulse" />
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
            <Package className="h-12 w-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-stone-900 mb-2">No listings yet</h3>
            <p className="text-stone-500 mb-6">Create your first listing to start selling!</p>
            <Link to="/create-listing">
              <Button className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white" disabled={!sellerCanList}>
                <Plus className="h-4 w-4 mr-2" />
                Create Listing
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {listings.map((listing, idx) => (
              <motion.div
                key={listing.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`bg-white border border-stone-200 rounded-sm p-4 flex flex-col sm:flex-row gap-4 ${
                  listing.status === 'sold' ? 'border-l-4 border-l-teal-600' : listing.status !== 'active' ? 'opacity-60' : ''
                }`}
                data-testid={`listing-item-${listing.id}`}
              >
                {/* Image */}
                <div className="w-full sm:w-32 h-32 flex-shrink-0 rounded-sm overflow-hidden bg-stone-100 relative">
                  <img
                    src={listing.images?.[0] || listing.after_images?.[0] || 'https://images.unsplash.com/photo-1527854176090-66db22836da9?w=200'}
                    alt={listing.title}
                    className={`w-full h-full object-cover ${listing.status === 'sold' ? 'opacity-70' : ''}`}
                  />
                  {listing.status === 'sold' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="bg-gradient-to-r from-sky-500 to-teal-500 text-white font-bold text-xs px-3 py-1 rounded shadow-lg transform -rotate-12">
                        SOLD
                      </span>
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <Link to={`/listing/${listing.id}`} className="hover:text-teal-600">
                        <h3 className={`font-semibold truncate ${listing.status === 'sold' ? 'text-stone-500' : 'text-stone-900'}`}>
                          {listing.title}
                        </h3>
                      </Link>
                      <p className="text-sm text-stone-500 mt-1 line-clamp-2">{listing.description}</p>
                    </div>
                    <span className={`price-tag text-xl whitespace-nowrap ${listing.status === 'sold' ? 'line-through text-stone-400' : ''}`}>
                      ${listing.price.toFixed(2)}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-stone-400">
                    <span className="capitalize">{listing.category?.replace('-', ' ')}</span>
                    <span>•</span>
                    <span className="capitalize">{listing.condition?.replace('-', ' ')}</span>
                    <span>•</span>
                    <span>{listing.location}</span>
                    {listing.is_restoration && (
                      <>
                        <span>•</span>
                        <span className="text-teal-600">Restoration</span>
                      </>
                    )}
                  </div>

                  {/* Status Badge */}
                  <div className="mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      listing.status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : listing.status === 'sold'
                        ? 'bg-teal-100 text-teal-600 font-semibold'
                        : 'bg-stone-100 text-stone-500'
                    }`}>
                      {listing.status === 'active' ? 'Active' : listing.status === 'sold' ? 'SOLD' : 'Paused'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex sm:flex-col gap-2 sm:justify-center">
                  {/* Mark as Sold / Unsold Button */}
                  {listing.status !== 'sold' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => markAsSold(listing)}
                      className="flex-1 sm:flex-none bg-green-50 border-green-600 text-green-700 hover:bg-green-100"
                      data-testid={`mark-sold-${listing.id}`}
                    >
                      <CheckCircle className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Mark Sold</span>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => markAsUnsold(listing)}
                      className="flex-1 sm:flex-none"
                      data-testid={`mark-unsold-${listing.id}`}
                    >
                      <Tag className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Relist</span>
                    </Button>
                  )}
                  
                  {listing.status !== 'sold' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleStatus(listing)}
                      className="flex-1 sm:flex-none"
                      data-testid={`toggle-status-${listing.id}`}
                    >
                      {listing.status === 'active' ? (
                        <><EyeOff className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Pause</span></>
                      ) : (
                        <><Eye className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Activate</span></>
                      )}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/edit-listing/${listing.id}`)}
                    className="flex-1 sm:flex-none"
                    data-testid={`edit-listing-${listing.id}`}
                  >
                    <Edit className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 sm:flex-none text-teal-600 hover:text-teal-600 hover:bg-teal-50"
                        data-testid={`delete-listing-${listing.id}`}
                      >
                        <Trash2 className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Delete</span>
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Listing</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{listing.title}"? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(listing.id)}
                          className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
